import { useMobilePanel } from '@/app-shell/layout/Navigation/mobile/use-mobile-panel';
import { isAgentHomeRailActive } from '@/app-shell/layout/Navigation/nav-v3/agent-home-panel-state';
import { HomeHoverPreview } from '@/app-shell/layout/Navigation/nav-v3/home-preview/home-hover-preview';
import { navV3Messages } from '@/app-shell/layout/Navigation/nav-v3/nav-v3.messages';
import { NavItem } from '@/app-shell/layout/Navigation/sidebar/nav-item';
import { isOnPrem } from '@/utils/buildProfile';
import { Heidi2EvidenceTourHomeStep, Heidi2HomeNavIcon } from '@heidi/agents';
import { WORK_CHAT_HOME_PATH } from '@heidi/agents/contracts';
import {
  pendingWorkHomeMode,
  pendingWorkHomeViewAtom,
  pendingWorkModeAtom,
  useBaseChatHref,
  useIsHeidi2ExploreLaterLive,
} from '@heidi/agents/data';
import { useSetAtom } from 'jotai';
import { House } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback, type Ref } from 'react';
import { useIntl } from 'react-intl';

/** Primary-sidebar Home destination; the Home canvas owns its panel toggle. */
export const HomeNavItem = () => {
  const intl = useIntl();
  const router = useRouter();
  const baseChatHref = useBaseChatHref();
  const { dismissMobileSidebar } = useMobilePanel();
  const setPendingWorkHomeView = useSetAtom(pendingWorkHomeViewAtom);
  const setPendingWorkMode = useSetAtom(pendingWorkModeAtom);
  const isOnHomeScreen = isAgentHomeRailActive(router.asPath, baseChatHref);
  // The nudges read SaaS billing, so on-prem keeps the plain icon.
  const isHeidi2ExploreLaterLive = useIsHeidi2ExploreLaterLive() && !isOnPrem;

  const onClick = useCallback(() => {
    if (baseChatHref === WORK_CHAT_HOME_PATH) {
      setPendingWorkHomeView('home');
      setPendingWorkMode(pendingWorkHomeMode);
    }
    dismissMobileSidebar();
  }, [baseChatHref, setPendingWorkHomeView, setPendingWorkMode, dismissMobileSidebar]);

  const renderNavItem = (ref: Ref<HTMLLIElement> | undefined, isPreviewOpen: boolean) => (
    <NavItem
      ref={ref}
      label={intl.formatMessage(navV3Messages.home)}
      icon={
        isHeidi2ExploreLaterLive ? (
          <Heidi2HomeNavIcon isOnHomeScreen={isOnHomeScreen} />
        ) : (
          <House />
        )
      }
      href={baseChatHref}
      onClick={onClick}
      activeMatch={baseChatHref === WORK_CHAT_HOME_PATH ? 'prefix' : 'exact'}
      // The preview remains active when focus moves into its content.
      isActive={isOnHomeScreen || isPreviewOpen}
      pinActive={isPreviewOpen}
      testId="sidebar-link-home"
    />
  );

  const navItem = (
    <HomeHoverPreview baseChatHref={baseChatHref}>
      {(ref, isPreviewOpen) => renderNavItem(ref, isPreviewOpen)}
    </HomeHoverPreview>
  );

  if (isOnPrem) return navItem;

  return (
    <Heidi2EvidenceTourHomeStep
      onShowMe={() => {
        onClick();
        void router.push(baseChatHref);
      }}
    >
      {navItem}
    </Heidi2EvidenceTourHomeStep>
  );
};
